import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import placeholderImage from 'assets/img/rambla-icon.png';
import { formatPrice } from 'utils/products';
import { getUniqueColorsForSelectedSize } from 'utils/getUniqueColorsForSelectedSize';
import { getUniqueSizes } from 'utils/getUniqueSizes';
import { SizePicker } from './SizePicker';
import { ColorPicker } from './ColorPicker';
import * as Ui from './AvailableProduct.styles';

const AvailableProduct = ({ product, onAdd, addedProducts = [] }) => {
  const { t } = useTranslation('app');
  const { name, child_products: childProducts } = product;
  const [selectedSizeUid, setSelectedSizeUid] = useState(childProducts[0]?.size?.uid);
  const [selectedColorUid, setSelectedColorUid] = useState(childProducts[0]?.color?.uid);

  const sizes = getUniqueSizes(childProducts);
  const colors = getUniqueColorsForSelectedSize(childProducts, selectedSizeUid);

  const selectedVariant =
    childProducts.find((p) => {
      if (selectedSizeUid && selectedColorUid) {
        return p.size?.uid === selectedSizeUid && p.color?.uid === selectedColorUid;
      }
      if (selectedSizeUid) {
        return p.size?.uid === selectedSizeUid;
      }
      if (selectedColorUid) {
        return p.color?.uid === selectedColorUid;
      }
      return false;
    }) ?? childProducts[0];

  const isProductAdded = addedProducts.some((parentProduct) =>
    parentProduct?.child_products?.some((child) => child.uid === selectedVariant?.uid),
  );

  const handleSizeChange = (sizeUid) => {
    setSelectedSizeUid(sizeUid);
    const newColors = getUniqueColorsForSelectedSize(childProducts, sizeUid);
    setSelectedColorUid(newColors[0]?.uid || null);
  };

  const handleColorChange = (colorUid) => {
    setSelectedColorUid(colorUid);
  };

  const handleAddClick = () => {
    if (selectedVariant) {
      onAdd(product, selectedVariant);
    }
  };

  if (!selectedVariant) {
    return null;
  }

  const imageSrc = selectedVariant.picture || placeholderImage;
  const isPlaceholder = !selectedVariant.picture;

  return (
    <Ui.StyledProductCard>
      <Ui.ProductImage alt="product" src={imageSrc} $isPlaceholder={isPlaceholder} />
      <Ui.ProductInfo>
        <Ui.ProductName>{name.toUpperCase()}</Ui.ProductName>
        {selectedVariant.price && selectedVariant.currency ? (
          <Ui.ProductPrice>
            {formatPrice(selectedVariant.price, selectedVariant.currency)}
          </Ui.ProductPrice>
        ) : null}
        <Ui.OptionsContainer>
          {sizes.length >= 1 && (
            <SizePicker
              sizes={sizes}
              selectedSizesUids={[selectedSizeUid]}
              onClick={handleSizeChange}
            />
          )}
          {colors.length >= 1 && (
            <ColorPicker
              colors={colors}
              selectedColors={[selectedColorUid]}
              onClick={handleColorChange}
            />
          )}
        </Ui.OptionsContainer>
        <Ui.ActionsContainer>
          <Ui.StyledButton disabled={isProductAdded} onClick={handleAddClick}>
            {t('add')}
          </Ui.StyledButton>
        </Ui.ActionsContainer>
      </Ui.ProductInfo>
    </Ui.StyledProductCard>
  );
};

AvailableProduct.propTypes = {
  product: PropTypes.object.isRequired,
  onAdd: PropTypes.func.isRequired,
  addedProducts: PropTypes.arrayOf(PropTypes.object),
};

export default AvailableProduct;
